import { Injectable, EventEmitter, Injector, NgZone } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Observer,
  of,
  Subject,
  Subscription,
  distinctUntilChanged,
  switchMap,
  tap,
} from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { environment } from '@environments/environment';

const SANDBOX_INTEGRITY =
  'sha384-fOaCPIhHNNFNHXHKks9iDyZiQKz4PCq/SIA9+IM4+ApcYI0bxLL+BVyabmiOLtJj';
const PRODUCTION_INTEGRITY =
  'sha384-JS/Q0U1PlEbjK05xn23jCiI9IGUnrXO4HmTcz+sEA6z+n4qfKYfPIzm1Knev67+G';

@Injectable({ providedIn: 'root' })
export class RiskJsScriptTag {
  private readonly document: Document | null;
  private readonly src_sandbox: string =
    'https://risk.sandbox.checkout.com/cdn/risk/2.1/risk.js';
  private readonly src_production: string =
    'https://risk.checkout.com/cdn/risk/2.1/risk.js';

  private readonly tokenChanged: EventEmitter<any>;
  private addRiskTokenSubject: Subject<any | null> | null;
  private readonly riskJsChanged: EventEmitter<any | null>;

  private readonly riskJsSubject: BehaviorSubject<any | null>;

  private readonly subscriptions: Subscription;

  constructor(
    private readonly zone: NgZone,
    private readonly injector: Injector,
    private readonly akitaRouterQuery: AkitaRouterQuery
  ) {
    this.subscriptions = new Subscription();

    if (this.akitaRouterQuery.isBrowser) {
      try {
        this.document = this.injector.get<Document | null>(DOCUMENT, null);
      } catch (err) {
        this.document = null;
      }
    } else {
      this.document = null;
    }

    this.tokenChanged = new EventEmitter();
    this.riskJsChanged = new EventEmitter();
    this.addRiskTokenSubject = null;
    this.riskJsSubject = new BehaviorSubject(null as any);
  }

  public stopSubscriptions(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  public get onTokenChange(): Observable<any | null> {
    return this.tokenChanged.asObservable().pipe(distinctUntilChanged());
  }
  public get instance(): any | null {
    return this.riskJsSubject.value;
  }

  public get instance$(): Observable<any | null> {
    return this.riskJsSubject.asObservable();
  }

  public promiseInstance(): Observable<any | null> {
    if (this.akitaRouterQuery.isBrowser) {
      const subscriptions = new Subscription();
      return new Observable((observer: Observer<any | null>) => {
        this.zone.runOutsideAngular(() => {
          subscriptions.add(
            this.injectIntoHead()
              .pipe(
                switchMap((riskJs: any | null) => {
                  if (riskJs) {
                    this.zone.run(() => {
                      this.riskJsSubject.next(riskJs);
                      observer.next(riskJs);
                      observer.complete();
                    });
                    if (subscriptions) {
                      subscriptions.unsubscribe();
                    }
                    return of(riskJs);
                  } else {
                    return this.riskJsChanged.pipe(
                      tap(() => {
                        if (!riskJs) {
                          const err: {
                            type: string;
                            code: string;
                            charge: string;
                            message: string;
                          } = {
                            type: 'invalid_request_error',
                            charge: '',
                            message: 'Checkout PublicKey NOT SET.',
                            code: 'CHECKOUT_COM_KEY_NOT_SET',
                          };
                          this.zone.run(() => {
                            observer.error(err);
                            observer.complete();
                          });
                          if (subscriptions) {
                            subscriptions.unsubscribe();
                          }
                        } else {
                          this.zone.run(() => {
                            this.riskJsSubject.next(riskJs);
                            observer.next(riskJs);
                            observer.complete();
                          });
                          if (subscriptions) {
                            subscriptions.unsubscribe();
                          }
                        }
                      })
                    );
                  }
                })
              )
              .subscribe({
                next: () => {},
                error: (err: unknown) => {
                  this.zone.run(() => {
                    observer.error(err);
                    observer.complete();
                  });
                  if (subscriptions) {
                    subscriptions.unsubscribe();
                  }
                },
              })
          );
        });
      });
    } else {
      return of(null);
    }
  }

  private injectIntoHead(): Observable<any | null> {
    if (this.akitaRouterQuery.isBrowser) {
      const subscriptions = new Subscription();
      return new Observable((observer: Observer<any | null>) => {
        if (this.addRiskTokenSubject && !this.addRiskTokenSubject.closed) {
          subscriptions.add(
            this.addRiskTokenSubject.asObservable().subscribe({
              next: (instance: any | null) => {
                observer.next(instance);
                observer.complete();

                if (subscriptions) {
                  subscriptions.unsubscribe();
                }
              },
              error: () => {
                observer.next(null);
                observer.complete();

                if (subscriptions) {
                  subscriptions.unsubscribe();
                }
              },
            })
          );
        } else {
          const checkout = this.grabRisk();
          if (checkout) {
            observer.next(checkout);
            observer.complete();

            if (subscriptions) {
              subscriptions.unsubscribe();
            }
            return;
          }

          if (!this.document) {
            // Document not Available in Server Rendering
            observer.error('CHECKOUT_COM_DOCUMENT_NOT_AVAILABLE');
            observer.complete();

            if (subscriptions) {
              subscriptions.unsubscribe();
            }
            return;
          }

          const head = this.getTargetTagDropElement();
          if (head) {
            const script = (this.document as any).createElement('script');
            script.setAttribute(
              'src',
              environment.production ? this.src_production : this.src_sandbox
            );
            script.setAttribute('nonce', '91b44c64ea46bb04fc29e3e0a83d939b');
            /*             script.setAttribute('name', 'dynamic-checkout-com-script');
             */
            script.setAttribute('type', 'text/javascript');
            script.setAttribute(
              'integrity',
              environment.production ? PRODUCTION_INTEGRITY : SANDBOX_INTEGRITY
            );
            script.setAttribute('crossorigin', 'anonymous');

            script.addEventListener('load', () => {
              const riskJs = this.grabRisk();
              if (this.addRiskTokenSubject && !this.addRiskTokenSubject.closed) {
                this.addRiskTokenSubject.next(riskJs);
                this.addRiskTokenSubject.complete();
                this.addRiskTokenSubject = null;
              }

              observer.next(riskJs);
              observer.complete();

              if (subscriptions) {
                subscriptions.unsubscribe();
              }
            });

            script.addEventListener('error', (error: unknown) => {
              if (this.addRiskTokenSubject && !this.addRiskTokenSubject.closed) {
                this.addRiskTokenSubject.error(error);
              } else {
                observer.error(error);
              }
              observer.complete();
              this.addRiskTokenSubject = null;

              if (subscriptions) {
                subscriptions.unsubscribe();
              }
            });

            this.addRiskTokenSubject = new Subject();
            head.append(script);
          } else {
            observer.error('CHECKOUT_COM_HEAD_NOT_AVAILABLE');
            observer.complete();

            if (subscriptions) {
              subscriptions.unsubscribe();
            }
          }
        }
      });
    } else {
      return of(null);
    }
  }

  private grabRisk(): any | null {
    if (this.akitaRouterQuery.isBrowser) {
      try {
        return (window as any).Risk;
      } catch {
        return null; // SSR?
      }
    } else {
      return null;
    }
  }

  private getTargetTagDropElement(): HTMLBodyElement | HTMLHeadElement | null {
    if (this.akitaRouterQuery.isBrowser && this.document) {
      const headElement = this.document.querySelectorAll('head');
      if (headElement.length > 0) {
        return headElement[0];
      }
      return this.document.querySelectorAll('body')[0];
    }

    return null;
  }
}
